import { parsePrimaryKeyAndSource, compareValuesNullsLast } from '@/components/shared-table/utils'
import {
  buildColumnDef,
  getFormattedValue,
  formatValueWithConversion,
  getModelPredictionRoot,
} from '@/components/shared-table/buildColumnDef'
import { getChemicalMassFromParams } from '@/components/shared-table/utils'
import { isExperimentalField } from '@/components/shared-table/buildColumnDef'
import { isModelColumnsVisible } from '@/components/combined-predict/modelAdapterService'
import {
  getPredictionDataBySource,
  getEcosarPredictionByProperty,
  getHydrolysisPredictionByProperty,
} from '@/components/combined-predict/models/epiSuite/utils'

const getValueToCompareWith = (value) => {
  const extractedValue = Array.isArray(value) ? (value[0]?.value ?? value[0]) : (value?.value ?? value)
  return extractedValue
}

const findPredictionForProperty = ({ modelId, predictionsByModelId, endpointId }) => {
  const predictionRoot = getModelPredictionRoot(predictionsByModelId, modelId)
  return !predictionRoot?.length ? null : predictionRoot?.find((prediction) => prediction.property === endpointId)
}

const getPredictionRawValueByProperty = ({ modelId, predictionsByModelId, fieldKey }) => {
  const { primaryKey: endpointId, sourceType } = parsePrimaryKeyAndSource(fieldKey, modelId)

  const predictionForProperty = findPredictionForProperty({
    modelId,
    predictionsByModelId,
    endpointId,
  })

  if (!predictionForProperty) return null

  if (endpointId?.startsWith('ecosar')) return getEcosarPredictionByProperty(predictionForProperty, sourceType)
  if (endpointId?.startsWith('hydrolysis')) return getHydrolysisPredictionByProperty(predictionForProperty, sourceType)

  const predictionDataByDataSource = getPredictionDataBySource(predictionForProperty, sourceType)

  return predictionDataByDataSource
}

const formatPredictionDisplayValue = (predictionValue, { originalUnit = null, unit = null, mass = null } = {}) => {
  const formatSinglePredictionValue = (rawPredictionValue) =>
    formatValueWithConversion({
      value: rawPredictionValue?.value ?? rawPredictionValue,
      fromUnit: originalUnit,
      toUnit: unit,
      mass: mass,
    })

  const formatPredictionSourceValue = (sourcePredictionValue) => {
    if (sourcePredictionValue?.length)
      return sourcePredictionValue.map((predictionEntry) => formatSinglePredictionValue(predictionEntry)).join(', ')
    return formatSinglePredictionValue(sourcePredictionValue)
  }

  return formatPredictionSourceValue(predictionValue)
}

const buildEpiSuiteDetail = ({ column, visibleColumnIds }) => {
  const modelId = column?.modelId
  return buildColumnDef({
    column,
    cellRenderer: 'EpiSuiteDetailDialog',
    hide: !isModelColumnsVisible(visibleColumnIds, modelId),
    cellRendererParams: (params) => ({
      modelId: modelId,
      epiSuitePredictions: getModelPredictionRoot(params?.data?.predictionsByModelId, modelId) ?? [],
    }),
    autoHeight: true,
    sortable: false,
    maxWidth: 70,
  })
}

const buildEpiSuiteColumnDef = ({ column, visibleColumnIds, tableSettings }) => {
  if (column?.id.endsWith('detail')) return buildEpiSuiteDetail({ column, visibleColumnIds })

  const isExperimental = isExperimentalField(column?.id)

  return buildColumnDef({
    column,
    visibleColumnIds,
    tableSettings,

    valueGetter: (params) =>
      getPredictionRawValueByProperty({
        modelId: column?.modelId,
        predictionsByModelId: params?.data?.predictionsByModelId,
        fieldKey: params?.colDef?.field,
      }),

    valueFormatter: (params) =>
      getFormattedValue({
        params,
        column,
        formatFunction: () =>
          formatPredictionDisplayValue(params?.value, {
            originalUnit: column?.originalUnit,
            unit: column?.unit,
            mass: getChemicalMassFromParams(params),
          }),
      }),

    comparator: (aValue, bValue, _nodeA, _nodeB, isDescending) => {
      const aValueToCompare = getValueToCompareWith(aValue)
      const bValueToCompare = getValueToCompareWith(bValue)
      return compareValuesNullsLast(aValueToCompare, bValueToCompare, isDescending)
    },

    ...(!isExperimental
      ? {
          tooltipComponent: 'EpiSuiteCardTooltip',
          tooltipComponentParams: (params) => ({
            predictionRoot: getModelPredictionRoot(params?.data?.predictionsByModelId, column?.modelId),
            property: parsePrimaryKeyAndSource(params?.colDef?.field, column?.modelId)?.primaryKey,
          }),
        }
      : {}),
  })
}

export { buildEpiSuiteColumnDef }
